import { readSnapshot, readSnapshotInfo, SIM_VERSION, SNAPSHOT_FORMAT_VERSION } from '@am/sim';
import { api, currentAccount } from './account';

/*
 * Saved games. Signed in, they are kept on the game server under the player's account
 * (single-player and co-op alike), so they follow the player to any device: phone, tablet or
 * computer. Without an account the game is played all the same, its saves kept on this device
 * (IndexedDB); signing in later moves them to the account (see importLocalSaves).
 *
 * A save is the simulation's own snapshot (the same bytes co-op uses to sync players), plus a
 * few facts for the list and a small picture. Snapshots carry a format version and the
 * simulation migrates old formats on load, so saves keep working after the game is updated.
 * Saves can also be exported as files and imported again.
 */

export type SaveMode = 'solo' | 'coop';

export interface SaveMeta {
  id: string;
  name: string;
  /** Real time it was saved (ms since 1970). */
  savedAt: number;
  /** Game ticks and money (cents) at the time. */
  time: number;
  money: number;
  simVersion: string;
  formatVersion: number;
  /** Small JPEG of the view when saved, or empty. */
  picture: string;
  /** True for the automatic save slot. */
  auto: boolean;
  /** Played alone or in co-op (older saves: alone). */
  mode?: SaveMode;
  /** The map (MapTheme; older saves: the green valley). */
  theme?: number;
}

/** Day-by-day figures kept with a save for the charts (see store DaySummary). */
export interface SaveHistory {
  history?: import('./store').DaySummary[];
}

export interface SaveRecord extends SaveMeta, SaveHistory {
  data: Uint8Array;
}

/** The single automatic save slot (overwritten every few minutes and when leaving a game). */
export const AUTOSAVE_ID = 'auto';
export const SAVE_FILE_EXTENSION = '.am27';

/** Every save (the account's, or this device's without one), newest first, without the data. */
export async function listSaves(): Promise<SaveMeta[]> {
  if (!currentAccount()) {
    const records = await localRecords();
    return records.map(({ data: _data, history: _history, ...meta }) => meta).sort(newestFirst);
  }
  const { saves } = await api<{ saves: SaveMeta[] }>('GET', '/saves');
  return saves.sort(newestFirst);
}

const newestFirst = (a: SaveMeta, b: SaveMeta) => b.savedAt - a.savedAt;

export async function loadSave(id: string): Promise<SaveRecord | undefined> {
  if (!currentAccount()) return localRecord(id);
  try {
    const { save } = await api<{ save: SaveMeta & SaveHistory & { data: string } }>(
      'GET',
      `/saves/${encodeURIComponent(id)}`,
    );
    return { ...save, data: fromBase64(save.data) };
  } catch (e) {
    if ((e as { status?: number }).status === 404) return undefined;
    throw e;
  }
}

export async function deleteSave(id: string): Promise<undefined> {
  if (!currentAccount()) {
    await localWrite((store) => store.delete(id));
    return undefined;
  }
  await api('DELETE', `/saves/${encodeURIComponent(id)}`);
  return undefined;
}

/**
 * Stores a snapshot under `id` (a new id when omitted) and returns its facts, with the account
 * achievements the server unlocked from it.
 */
export async function writeSave(
  data: Uint8Array,
  options: {
    id?: string;
    name: string;
    picture?: string;
    history?: SaveHistory['history'];
    mode?: SaveMode;
    /** The map (the server reads it from the save itself). */
    theme?: number;
  },
): Promise<SaveMeta & { unlocked: string[] }> {
  const info = readSnapshotInfo(data); // a save of this game, or throw before uploading
  const id = options.id ?? `save-${Date.now().toString(36)}`;
  if (!currentAccount()) {
    // On this device: the same facts the server would keep.
    const record: SaveRecord = {
      id,
      name: options.name,
      savedAt: Date.now(),
      time: info.time,
      money: info.money,
      simVersion: info.simVersion,
      formatVersion: info.formatVersion,
      picture: options.picture ?? '',
      auto: id === AUTOSAVE_ID,
      mode: options.mode ?? 'solo',
      theme: options.theme ?? 0,
      history: options.history ?? [],
      data,
    };
    await localWrite((store) => store.put(record));
    const { data: _data, history: _history, ...meta } = record;
    return { ...meta, unlocked: [] };
  }
  const { save, unlocked } = await api<{ save: SaveMeta; unlocked?: string[] }>(
    'PUT',
    `/saves/${encodeURIComponent(id)}`,
    {
      name: options.name,
      picture: options.picture ?? '',
      history: options.history ?? [],
      mode: options.mode ?? 'solo',
      data: toBase64(data),
    },
  );
  return { ...save, unlocked: unlocked ?? [] };
}

/** Whether this build can open the save (saves from a newer game version cannot). */
export function canLoad(meta: Pick<SaveMeta, 'formatVersion'>): boolean {
  return meta.formatVersion <= SNAPSHOT_FORMAT_VERSION;
}

/** Downloads a save as a file. */
export async function exportSave(id: string): Promise<void> {
  const save = await loadSave(id);
  if (!save) return;
  const blob = new Blob([save.data as Uint8Array<ArrayBuffer>], {
    type: 'application/octet-stream',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName(save.name)}${SAVE_FILE_EXTENSION}`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Reads a save file chosen by the player. Throws if it is not a save of this game. */
export async function importSave(file: File): Promise<SaveMeta> {
  const data = new Uint8Array(await file.arrayBuffer());
  readSnapshotInfo(data); // validates the header
  const name = file.name.replace(/\.am27$/i, '') || 'Import';
  // Its map, for the list (a one-off: reading the whole world is fine here).
  let theme = 0;
  try {
    theme = readSnapshot(data).theme;
  } catch {
    // Unreadable in full: listed as the green valley; loading it will tell.
  }
  return writeSave(data, { name, theme });
}

function fileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '').trim() || 'airport';
}

/** For the list: the version a save was made with, when it differs from this build. */
export function versionNote(meta: SaveMeta): string | null {
  return meta.simVersion === SIM_VERSION ? null : meta.simVersion;
}

function toBase64(bytes: Uint8Array): string {
  let text = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(text);
}

function fromBase64(text: string): Uint8Array {
  const raw = atob(text);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

// ---------------------------------------------------------------------------------------------
// Saves on this device (IndexedDB): a player's without an account, and those from before
// accounts

const LOCAL_DB = 'airport-manager-2027';
const LOCAL_STORE = 'saves';
/** Per account, the local saves already moved to it (id → savedAt). */
const MOVED_KEY = 'am27.localMoved';

/**
 * This device's save database, its store made the first time it is needed (at whatever version
 * the database already is); null where the device keeps none.
 */
function openLocal(version?: number): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = version ? indexedDB.open(LOCAL_DB, version) : indexedDB.open(LOCAL_DB);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LOCAL_STORE)) {
        db.createObjectStore(LOCAL_STORE, { keyPath: 'id' });
      }
    };
    request.onerror = () => resolve(null);
    request.onsuccess = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(LOCAL_STORE) || version) {
        resolve(db);
        return;
      }
      // A database without the store (made by something else): one version up, with it.
      const next = db.version + 1;
      db.close();
      void openLocal(next).then(resolve);
    };
  });
}

function localRecords(): Promise<SaveRecord[]> {
  return openLocal().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) {
          resolve([]);
          return;
        }
        const get = db.transaction(LOCAL_STORE, 'readonly').objectStore(LOCAL_STORE).getAll();
        get.onsuccess = () => {
          db.close();
          resolve(get.result as SaveRecord[]);
        };
        get.onerror = () => {
          db.close();
          resolve([]);
        };
      }),
  );
}

function localRecord(id: string): Promise<SaveRecord | undefined> {
  return openLocal().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) {
          resolve(undefined);
          return;
        }
        const get = db.transaction(LOCAL_STORE, 'readonly').objectStore(LOCAL_STORE).get(id);
        get.onsuccess = () => {
          db.close();
          resolve(get.result as SaveRecord | undefined);
        };
        get.onerror = () => {
          db.close();
          resolve(undefined);
        };
      }),
  );
}

/** Writes to this device's saves; fails when the device keeps none (private browsing, full). */
function localWrite(change: (store: IDBObjectStore) => IDBRequest): Promise<void> {
  return openLocal().then(
    (db) =>
      new Promise((resolve, reject) => {
        if (!db) {
          reject(new Error('This device cannot keep saves'));
          return;
        }
        const tx = db.transaction(LOCAL_STORE, 'readwrite');
        change(tx.objectStore(LOCAL_STORE));
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = tx.onabort = () => {
          db.close();
          reject(tx.error ?? new Error('Saving on this device failed'));
        };
      }),
  );
}

/**
 * Moves this device's saves to the signed-in account: those played without an account (and
 * from before accounts), each once, again only if it was saved since. The local copies stay
 * where they are. Returns how many were moved.
 */
export async function importLocalSaves(): Promise<number> {
  const account = currentAccount();
  if (!account) return 0;
  const movedAll = (() => {
    try {
      return JSON.parse(localStorage.getItem(MOVED_KEY) ?? '{}') as Record<
        string,
        Record<string, number>
      >;
    } catch {
      return {};
    }
  })();
  const moved = movedAll[account.username] ?? {};
  const records = await localRecords();
  let count = 0;
  for (const r of records) {
    if ((moved[r.id] ?? -1) >= r.savedAt) continue;
    try {
      await writeSave(r.data, {
        // Their own ids; this device's autosave gets a slot of its own beside the account's.
        id: r.id === AUTOSAVE_ID ? 'local-auto' : r.id,
        name: r.name,
        picture: r.picture,
        history: r.history,
        mode: r.mode,
        theme: r.theme,
      });
      moved[r.id] = r.savedAt;
      count++;
    } catch {
      // An unreadable or oversized save stays only on this device.
    }
  }
  try {
    localStorage.setItem(MOVED_KEY, JSON.stringify({ ...movedAll, [account.username]: moved }));
  } catch {
    // Without storage they are moved again next time (overwritten, not duplicated).
  }
  return count;
}
